import { useMount, useUnmount } from '@apia/util';
import React from 'react';
import { shallowEqual } from 'react-redux';
import { FormContext } from '../form/FormContext';
import { PropsSelectorUndefinedObject } from '@apia/util';
import { TValidationField } from '../types';
import { useFieldSelector, validationsStore } from '../validationStore';

export type TUseField<ValueType = unknown, SubmitValueType = ValueType> = Omit<
  TValidationField<ValueType, SubmitValueType>,
  'error' | 'value' | 'isTouched'
> & {
  /**
   * Con esta propiedad es posible evitar que el campo
   * se registre cuando ya fue registrado previamente en
   * el store. De esta forma, se evitaría escribir sobre
   * un valor que ya estaba seteado anteriormente.
   */
  avoidRegisterIfExists?: boolean;
  /**
   * Si se está fuera de contexto de validación, se puede pasar un formName
   * para evitar que el hook lo intente tomar de contexto.
   */
  formName?: string;
  initialValue?: ValueType;
  removeOnUnmount?: boolean;
};

/**
 * Este hook permite registrar un field para ser validado,
 * o simplemente para recolectar su valor, e incluso aplicar
 * una función de transformación de dicho valor antes de
 * hacer submit.
 *
 * @returns
 * *error:* Es null mientras que no haya errores de
 * validación, y un string en caso de que si lo haya.
 *
 * *value:* Es el valor actual del campo.
 *
 * *name:* Debería utilizarse para dar * valor al atributo
 * name o id del contenedor que debe recibir el foco en caso
 * de que la validación fallara. (Esto es, cuando se deba
 * realizar foco porque la validación falló, la librería
 * intentará encontrar un campo con el selector
 * [name=${name}, #${name}] dentro del formulario).
 *
 * *onChange:* Se utiliza para setear el valor del campo,
 * puede utilizarse directamente como callback para la
 * propiedad onChange que aceptan la mayoría de los campos,
 * ya que intentará setear el valor con ev.target.value
 * y si esa propiedad no está definida, lo hará directamente
 * con ev.
 *
 * @example
 *  // Si se desea un ejemplo más complejo, vea el componente
 *  // Invitations.tsx que se encuentra dentro del directorio
 *  // de la librería.
 *
 *  const inputProps = useField<string>({
      name: 'name',
      validationRules: {
        required: true,
        minLength: 5,
        maxLength: 10
      },
      initialValue: metadata.value,
 *  });

    return <Box>
      <Input {...inputProps} />
      <ValidationError name="name" />
    </Box>
 */
export function useField<ValueType = unknown, SubmitValueType = ValueType>(
  fieldDefinition: TUseField<ValueType, SubmitValueType>,
) {
  const formContext = React.useContext(FormContext);
  const formName = fieldDefinition.formName ?? formContext.name;

  if (!formName) {
    console.error(fieldDefinition);
    throw new Error(
      `useField: It was not possible to determine the formName in field with name ${fieldDefinition.name}`,
    );
  }

  React.useMemo(() => {
    validationsStore.registerField(
      formName,
      {
        ...fieldDefinition,
        ...(fieldDefinition.initialValue !== undefined
          ? { value: fieldDefinition.initialValue }
          : null),
      },
      {
        avoidPropagation: true,
        registerIfExists: !fieldDefinition.avoidRegisterIfExists,
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useMount(() => {
    if (
      fieldDefinition.initialValue !== undefined &&
      ((!validationsStore.getFormStore(formName)?.avoidFieldsOverride &&
        !fieldDefinition.avoidRegisterIfExists) ||
        validationsStore.getFieldStore(formName, fieldDefinition.name) ===
          undefined)
    )
      validationsStore.setFieldValue(
        formName,
        fieldDefinition.name,
        fieldDefinition.initialValue,
      );
  });

  useUnmount(() => {
    if (fieldDefinition.removeOnUnmount)
      validationsStore.unregisterField(formName, fieldDefinition.name);
  });

  const {
    name,
    value: storeValue,
    error,
    isTouched,
  } = useFieldSelector<
    ValueType,
    SubmitValueType,
    TValidationField<ValueType, SubmitValueType>
  >(
    formName,
    fieldDefinition.name,
    (props) => {
      return props;
    },
    (a, b) => {
      if (
        a === PropsSelectorUndefinedObject &&
        fieldDefinition.initialValue !== undefined
      )
        return true;
      return shallowEqual(a, b);
    },
  );

  const [localValue, setLocalValue] = React.useState<ValueType | undefined>(
    storeValue ?? fieldDefinition.initialValue,
  );

  React.useEffect(() => {
    setLocalValue(storeValue);
  }, [storeValue]);

  const onChange = React.useCallback(
    (ev: React.ChangeEvent | ValueType) => {
      const reactChangeEvent = ev as React.ChangeEvent<HTMLInputElement>;
      let newValue: ValueType;
      if (
        typeof reactChangeEvent === 'object' &&
        reactChangeEvent &&
        reactChangeEvent.target !== undefined &&
        reactChangeEvent.target.value !== undefined
      ) {
        newValue = reactChangeEvent.target.value as unknown as ValueType;
      } else newValue = ev as ValueType;

      setLocalValue(newValue);
      validationsStore.setFieldValue(formName, fieldDefinition.name, newValue);
    },
    [fieldDefinition.name, formName],
  );

  return {
    error,
    isTouched,
    name,
    onChange,
    updateValidations: React.useCallback(
      ({
        validationFunction,
        validationRules,
      }: Pick<TValidationField, 'validationRules' | 'validationFunction'>) => {
        validationsStore.updateField(formName, {
          name: fieldDefinition.name,
          validationFunction,
          validationRules,
        });
      },
      [formName, fieldDefinition.name],
    ),
    value: localValue ?? (fieldDefinition.initialValue as ValueType),
  };
}
